import type { Customer, CustomerSummary } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, User as UserIcon } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Select, Sheet } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatPhone, shortDate } from "../theme";

/** CustomerSummary carries `kind` once helpers.customerSummary sets it; until then the chip falls back to a name heuristic. */
const isBusiness = (c: CustomerSummary) => {
  const kind = (c as CustomerSummary & { kind?: string }).kind;
  if (kind) return kind === "business";
  return /\b(llc|inc\.?|corp\.?|co\.|company|services|shop|garage|auto sales)\b/i.test(c.name);
};

export function CustomersScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const [params, setParams] = useSearchParams();
  const [qText, setQText] = useState("");
  const q = useQuery({ queryKey: ["customers", qText], queryFn: async () => (await callAction<"agency.customer.search", CustomerSummary[]>("agency.customer.search", { q: qText, limit: 100 })).result });
  const open = params.get("new") === "1";
  const setOpen = (v: boolean) => setParams(v ? { new: "1" } : {});
  const userName = (id?: string) => config.users.find((u) => u.id === id)?.name.split(" ")[0] ?? "—";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold mr-auto">{t("customers.title")}</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> {t("customers.new")}
        </Button>
      </div>
      <Input value={qText} onChange={(e) => setQText(e.target.value)} placeholder={t("customers.search")} aria-label={t("customers.search")} />
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? <EmptyState title={t("customers.none")} action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> {t("customers.new")}</Button>} /> : null}
      {q.data && q.data.length > 0 ? (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                <th className="px-4 py-3">{t("common.name")}</th>
                <th className="px-4 py-3 hidden sm:table-cell">{t("common.phone")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("common.city")}</th>
                <th className="px-4 py-3">{t("common.policies")}</th>
                <th className="px-4 py-3 hidden sm:table-cell">{t("common.nextRenewal")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("common.owner")}</th>
                <th className="px-4 py-3">{t("common.language")}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <tr key={c.id} className="border-t border-ink/5 hover:bg-primary/5">
                  <td className="px-4 py-2">
                    <Link to={`/customers/${c.id}`} className="font-semibold hover:underline inline-flex items-center gap-2 min-h-[36px]">
                      {isBusiness(c) ? <Building2 className="h-4 w-4 text-ink/40" aria-label={t("customers.kind.business")} /> : <UserIcon className="h-4 w-4 text-ink/40" aria-label={t("customers.kind.person")} />}
                      {c.name}
                    </Link>
                    <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                      {isBusiness(c) ? <Badge>{t("customers.kind.business")}</Badge> : null}
                      {c.tags.includes("imported") ? <Badge tone="blue">{t("customers.imported")}</Badge> : null}
                      {c.tags.filter((x) => x !== "imported" && x !== "sample").slice(0, 2).map((x) => <Badge key={x}>{x}</Badge>)}
                    </span>
                    <span className="sm:hidden block text-xs text-ink/55">{formatPhone(c.phone)}</span>
                  </td>
                  <td className="px-4 py-2 hidden sm:table-cell">{formatPhone(c.phone) ?? "—"}</td>
                  <td className="px-4 py-2 hidden md:table-cell">{c.city ?? "—"}</td>
                  <td className="px-4 py-2">
                    <Badge tone={c.status === "active" ? "green" : c.status === "prospect" ? "blue" : "neutral"}>{c.policyCount} · {t(`customers.status.${c.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-2 hidden sm:table-cell">{shortDate(c.nextRenewalAt, locale)}</td>
                  <td className="px-4 py-2 hidden md:table-cell">{userName(c.ownerUserId)}</td>
                  <td className="px-4 py-2"><Badge tone={c.preferredLocale === "es" ? "amber" : "neutral"}>{c.preferredLocale.toUpperCase()}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
      <NewCustomerSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

export function NewCustomerSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const { config } = useConfig();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const blank = { kind: "person" as "person" | "business", businessName: "", firstName: "", lastName: "", email: "", phone: "", dob: "", line1: "", line2: "", city: "", state: config.agency.state ?? "TX", zip: "", preferredLocale: "en" as "en" | "es", source: "phone", line: "auto" };
  const [form, setForm] = useState(blank);
  const set = <K extends keyof typeof blank>(k: K, v: (typeof blank)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const m = useMutation({
    mutationFn: () => {
      const addressFilled = form.line1.trim() || form.city.trim() || form.zip.trim();
      return callAction<"agency.customer.create", Customer>("agency.customer.create", {
        kind: form.kind,
        businessName: form.kind === "business" ? form.businessName.trim() : undefined,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email || undefined,
        phone: form.phone || undefined,
        dob: form.kind === "person" && form.dob ? form.dob : undefined,
        address: addressFilled ? { line1: form.line1.trim(), line2: form.line2.trim() || undefined, city: form.city.trim(), state: form.state.trim().toUpperCase().slice(0, 2) || "TX", zip: form.zip.trim() } : undefined,
        preferredLocale: form.preferredLocale,
        source: form.source,
        line: form.line as "auto",
      });
    },
    onSuccess: (r) => {
      toast({ title: t("customers.created"), body: r.result.kind === "business" ? r.result.businessName : `${r.result.firstName} ${r.result.lastName}`, tone: "success" });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      setForm(blank);
      onClose();
      navigate(`/customers/${r.result.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("customers.new")}>
      <form onSubmit={submit} className="space-y-3">
        <div className="inline-flex rounded-xl border border-ink/15 overflow-hidden" role="group" aria-label={t("customers.kind")}>
          {(["person", "business"] as const).map((k) => (
            <button key={k} type="button" onClick={() => set("kind", k)} aria-pressed={form.kind === k} className={`inline-flex min-h-[40px] items-center gap-1 px-3 text-sm font-semibold ${form.kind === k ? "bg-ink text-white" : "text-ink/70 hover:bg-ink/5"}`}>
              {k === "person" ? <UserIcon className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}{t(`customers.kind.${k}`)}
            </button>
          ))}
        </div>
        {form.kind === "business" ? <Input label={t("customers.businessName")} required value={form.businessName} onChange={(e) => set("businessName", e.target.value)} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Input label={form.kind === "business" ? t("customers.business.contactName") : t("customers.firstName")} required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
          <Input label={t("customers.lastName")} required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
        </div>
        <Input label={t("common.phone")} type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(915) 555-0100" />
        <Input label={t("customers.email")} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        {form.kind === "person" ? <Input label={t("customers.dob")} type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /> : null}
        <Input label={t("customers.address.line1")} value={form.line1} onChange={(e) => set("line1", e.target.value)} />
        <div className="grid grid-cols-[1fr_72px_110px] gap-3">
          <Input label={t("customers.address.city")} value={form.city} onChange={(e) => set("city", e.target.value)} />
          <Input label={t("customers.address.state")} maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} />
          <Input label={t("customers.address.zip")} inputMode="numeric" value={form.zip} onChange={(e) => set("zip", e.target.value)} />
        </div>
        <Select label={t("customers.preferredLanguage")} value={form.preferredLocale} onChange={(e) => set("preferredLocale", e.target.value as "en" | "es")}>
          <option value="en">{t("common.english")}</option>
          <option value="es">{t("common.spanish")}</option>
        </Select>
        <Select label={t("customers.lineInterest")} value={form.line} onChange={(e) => set("line", e.target.value)}>
          {config.agency.lines.map((l) => (
            <option key={l} value={l}>{l.replace(/_/g, " ")}</option>
          ))}
        </Select>
        <Select label={t("customers.source")} value={form.source} onChange={(e) => set("source", e.target.value)}>
          <option value="phone">Phone</option>
          <option value="walk_in">Walk-in</option>
          <option value="referral">Referral</option>
          <option value="website">Website</option>
        </Select>
        <Button type="submit" size="lg" className="w-full" loading={m.isPending}>{t("common.save")}</Button>
      </form>
    </Sheet>
  );
}
